import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FaBars, FaTimes, FaSearch } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import LanguageSwitcher from './LanguageSwitcher';
import { useSearch } from '../context/SearchContext';

const HeaderContainer = styled.header`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--secondary-200);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: var(--z-fixed);
  transition: all var(--transition-normal);
`;

const Nav = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--space-md);
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  transition: opacity var(--transition-fast);
  height: 50px;

  &:hover {
    opacity: 0.8;
  }

  img {
    height: 100%;
    width: auto;
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-xl);

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: var(--secondary-700);
  font-weight: 500;
  text-decoration: none;
  padding: var(--space-sm) 0;
  position: relative;
  transition: color var(--transition-fast);

  &:hover {
    color: var(--primary-600);
  }

  &.active {
    color: var(--primary-600);
    
    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      right: 0;
      height: 2px;
      background: var(--primary-600);
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--secondary-700);
  cursor: pointer;
  padding: var(--space-sm);
  border-radius: var(--radius-sm);
  transition: all var(--transition-fast);

  &:hover {
    background: var(--secondary-100);
    color: var(--primary-600);
  }

  &:focus {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 70px;
  left: 0;
  right: 0;
  background: white;
  border-bottom: 1px solid var(--secondary-200);
  padding: var(--space-lg) var(--space-md);
  display: none;
  box-shadow: var(--shadow-lg);

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileNavLink = styled(Link)`
  display: block;
  color: var(--secondary-700);
  font-weight: 500;
  text-decoration: none;
  padding: var(--space-md) 0;
  border-bottom: 1px solid var(--secondary-200);
  transition: color var(--transition-fast);

  &:hover {
    color: var(--primary-600);
  }

  &.active {
    color: var(--primary-600);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const CTAButton = styled(Link)`
  background: var(--primary-600);
  color: white;
  padding: var(--space-sm) var(--space-lg);
  border-radius: var(--radius-md);
  font-weight: 500;
  text-decoration: none;
  transition: all var(--transition-fast);

  &:hover {
    background: var(--primary-700);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }
`;

const SearchButton = styled.button`
  background: none;
  border: 1px solid var(--secondary-300);
  color: var(--secondary-700);
  padding: var(--space-sm) var(--space-md);
  border-radius: var(--radius-md);
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  transition: all var(--transition-fast);
  font-size: 0.9rem;

  &:hover {
    background: var(--secondary-50);
    border-color: var(--primary-400);
    color: var(--primary-600);
  }

  &:focus {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const SearchButtonIcon = styled(FaSearch)`
  font-size: 0.9rem;
`;

const Header = () => {
  const { t } = useTranslation();
  const { openSearch } = useSearch();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const mobileMenuRef = useRef(null);
  const mobileMenuButtonRef = useRef(null);

  const navItems = [
    { path: '/', label: t('common.home') },
    { path: '/services', label: t('common.services') },
    { path: '/blog', label: t('common.blog') },
    { path: '/contact', label: t('common.contact') },
  ];

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) &&
          mobileMenuButtonRef.current && !mobileMenuButtonRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Trap focus within mobile menu
      const focusableElements = mobileMenuRef.current?.querySelectorAll(
        'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements && focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
        mobileMenuButtonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileMenuOpen]);

  return (
    <HeaderContainer>
      <Nav>
        <Logo to="/" onClick={scrollToTop} aria-label={t('accessibility.homePage')}>
          <img src="/logo.svg" alt="ScrumNX" />
        </Logo>
        
        <NavLinks role="navigation" aria-label="Main navigation">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={scrollToTop}
              className={isActive(item.path) ? 'active' : ''}
              aria-current={isActive(item.path) ? 'page' : undefined}
            >
              {item.label}
            </NavLink>
          ))}
          <SearchButton 
            onClick={openSearch}
            aria-label={t('search.ariaLabel')}
            title={`${t('search.ariaLabel')} (${navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+K)`}
          >
            <SearchButtonIcon />
            <span style={{ fontSize: '0.75rem', color: 'var(--secondary-500)' }}>
              {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}K
            </span>
          </SearchButton>
          <LanguageSwitcher />
          <CTAButton to="/contact" onClick={scrollToTop} aria-label={t('accessibility.getStartedServices')}>{t('header.getStarted')}</CTAButton>
        </NavLinks>

        <MobileMenuButton 
          onClick={toggleMobileMenu}
          ref={mobileMenuButtonRef}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-menu"
          aria-label={mobileMenuOpen ? t('accessibility.closeNavigationMenu') : t('accessibility.openNavigationMenu')}
        >
          {mobileMenuOpen ? <FaTimes /> : <FaBars />}
        </MobileMenuButton>
      </Nav>

      <AnimatePresence>
        {mobileMenuOpen && (
          <MobileMenu
            ref={mobileMenuRef}
            id="mobile-menu"
            role="navigation"
            aria-label={t('accessibility.mobileNavigation')}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {navItems.map((item) => (
              <MobileNavLink
                key={item.path}
                to={item.path}
                className={isActive(item.path) ? 'active' : ''}
                onClick={() => {
                  setMobileMenuOpen(false);
                  scrollToTop();
                }}
                aria-current={isActive(item.path) ? 'page' : undefined}
              >
                {item.label}
              </MobileNavLink>
            ))}
            <CTAButton 
              to="/contact"
              onClick={() => {
                setMobileMenuOpen(false);
                scrollToTop();
              }}
              style={{ display: 'inline-block', marginTop: 'var(--space-md)' }}
              aria-label={t('accessibility.getStartedServices')}
            >
              {t('header.getStarted')}
            </CTAButton>
          </MobileMenu>
        )}
      </AnimatePresence>
    </HeaderContainer>
  );
};

export default Header;